import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { apiRoutes } from '../../../api';
import { getConfig } from '../../../config/environment';
import { mapWinnersFromApi } from '../../../utils/winnersApi';
import { parseDrawDate, getDrawDateValue, getDrawIanaTimeZone } from '../../../utils/dateFormat';
import { useLanguage } from '../../../contexts/LanguageContext';

const DrawsPageContext = createContext(null);

const LANG_TO_LOCALE = { en: 'en-US', fr: 'fr-CA', es: 'es-ES' };

export function DrawsPageProvider({ children }) {
  const { language } = useLanguage();
  const locale = LANG_TO_LOCALE[language] || language || 'en-US';

  const [activeTab, setActiveTab] = useState('upcoming');
  const [upcomingDraws, setUpcomingDraws] = useState([]);
  const [pastDraws, setPastDraws] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const formatUpcoming = (draw, index) => {
      const dateValue = getDrawDateValue(draw);
      const date = parseDrawDate(draw);
      const isValidDate = !!date;
      // Display in the organization's timezone (like the homepage) when known.
      const ianaTz = getDrawIanaTimeZone(draw);
      const tzOpts = ianaTz ? { timeZone: ianaTz } : {};
      // Legacy API rows only carry a pre-formatted date string with no time —
      // don't fabricate a time from a midnight parse.
      const hasTime = typeof dateValue === 'string'
        ? /\d{1,2}:\d{2}/.test(dateValue)
        : isValidDate;

      return {
        id: draw.id || draw.Id || index + 1,
        day: isValidDate
          ? date.toLocaleDateString(locale, { weekday: 'long', ...tzOpts }).toUpperCase()
          : 'TBD',
        date: isValidDate
          ? date.toLocaleDateString(locale, { month: 'short', day: 'numeric', ...tzOpts }).toUpperCase()
          : 'TBD',
        fullDate: isValidDate
          ? date.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric', ...tzOpts })
          : (dateValue || 'TBD'),
        time: isValidDate && hasTime
          ? date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', ...tzOpts })
          : 'TBD',
        jackpot: Number(draw.amount ?? draw.Amount ?? draw.currentJackpot ?? 0),
        weeklyPrize: Number(
          draw.weekly_prize ??
          draw.weeklyPrize ??
          draw.secondary_prize ??
          draw.secondaryPrize ??
          draw.weeklyPrizeResolvedAmount ??
          draw.weeklyPrizeAmount ??
          draw.currentSecondaryJackpot ??
          draw.CurrentSecondaryJackpot ??
          draw.actual_weekly_amount ??
          draw.actualWeeklyAmount ??
          draw.ActualWeeklyAmount ??
          0
        ),
        ticketsSold: draw.ticketsSold ?? draw.TicketsSold ?? draw.totalTicketsSold ?? null,
        status: draw.status || draw.Status || 'upcoming',
        drawNumber: draw.drawNumber ?? draw.draw_number ?? draw.DrawNumber ?? null,
        gameNumber: draw.gameNumber ?? draw.GameNumber ?? null,
        weekNumber: draw.weekNumber ?? draw.WeekNumber ?? null,
        gameType: draw.gameType ?? draw.game_type ?? draw.GameType ?? null,
        // Prize-draw fields — used by the upcoming grid to render the prize name as the
        // headline and the dollar amount as an estimated value, instead of a jackpot.
        isPrize: draw.isPrize ?? draw.is_prize ?? draw.IsPrize ?? false,
        prizeName: draw.prizeName ?? draw.prize_name ?? draw.PrizeName ?? draw.name ?? draw.Name ?? null,
        prizeDescriptions: draw.prizeDescriptions ?? draw.prize_descriptions ?? draw.PrizeDescriptions ?? null
      };
    };

    const formatWinnerAsPast = (winner, index, drawsForLookup = []) => {
      const drawId = winner.drawId || winner.DrawId || winner.id || winner.Id;
      const drawRecord = drawId
        ? drawsForLookup.find((d) => d.id === drawId || d.drawId === drawId || d.DrawId === drawId)
        : null;
      const isPrize = winner.isPrize ?? drawRecord?.isPrize ?? drawRecord?.is_prize ?? false;
      const firstDescription = (descs) => {
        if (!descs || typeof descs !== 'object') return null;
        for (const v of Object.values(descs)) {
          if (v && String(v).trim()) return String(v).trim();
        }
        return null;
      };

      const prizeName = isPrize
        ? (
            drawRecord?.prizeName ||
            drawRecord?.prize_name ||
            firstDescription(drawRecord?.prizeDescriptions) ||
            firstDescription(drawRecord?.prize_descriptions) ||
            winner.prize ||
            winner.prizeName ||
            winner.PrizeName ||
            firstDescription(winner.prizeDescriptions) ||
            firstDescription(winner.prize_descriptions) ||
            null
          )
        : null;

      return {
        id: winner.id || winner.Id || `winner-${index}`,
        type: 'winner',
        drawNumber: winner.drawNumber || winner.DrawNumber || drawRecord?.drawNumber || drawRecord?.draw_number || null,
        drawLabel: winner.draw || winner.Draw || (winner.drawNumber ? `Draw #${winner.drawNumber}` : 'Draw'),
        date: winner.date || winner.Date || 'TBD',
        sortDate: (() => {
          const raw = winner.date || winner.Date;
          if (!raw) return new Date(0);
          const d = new Date(raw);
          return !Number.isNaN(d.getTime()) ? d : new Date(0);
        })(),
        jackpot: Number(winner.amount ?? winner.Amount ?? 0),
        winner: winner.name || winner.Name || 'Winner',
        ticketNumber: winner.ticketNumber || winner.TicketNumber || '—',
        totalTickets: winner.totalTicketsSold ?? winner.totalTickets ?? null,
        cardDrawn: winner.cardDrawn || null,
        cardNumber: winner.cardNumber || null,
        cardSuit: winner.cardSuit || null,
        isWinner: winner.isWinner !== false,
        isSecondaryPrize: winner.isSecondaryPrize ?? false,
        secondaryPrize: winner.secondaryPrize ?? null,
        isPrize,
        prize: prizeName,
        gameType: winner.gameType || null
      };
    };

    const resolvePrizeNameFromDraw = (draw) => {
      const descs = draw.prizeDescriptions ?? draw.prize_descriptions ?? draw.PrizeDescriptions ?? null;
      if (descs && typeof descs === 'object') {
        for (const v of Object.values(descs)) {
          if (v && String(v).trim()) return String(v).trim();
        }
      }
      return draw.prizeName ?? draw.prize_name ?? draw.PrizeName ?? draw.name ?? draw.Name ?? null;
    };

    const formatExpiredAsPast = (draw, index) => {
      const dateValue = getDrawDateValue(draw);
      const date = parseDrawDate(draw);
      const isValidDate = !!date;
      const ianaTz = getDrawIanaTimeZone(draw);
      const tzOpts = ianaTz ? { timeZone: ianaTz } : {};
      const isPrize = draw.isPrize ?? draw.is_prize ?? draw.IsPrize ?? false;

      return {
        id: draw.id || draw.Id || `expired-${index}`,
        type: 'expired',
        drawLabel: draw.name || draw.Name || draw.title || 'Draw',
        date: isValidDate
          ? date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', ...tzOpts })
          : (dateValue || 'TBD'),
        sortDate: isValidDate ? date : new Date(0),
        jackpot: Number(draw.amount ?? draw.Amount ?? draw.currentJackpot ?? 0),
        isPrize,
        prize: isPrize ? resolvePrizeNameFromDraw(draw) : null,
        prizeDescriptions: draw.prizeDescriptions ?? null,
        status: draw.status || draw.Status || 'undrawn'
      };
    };

    const loadDraws = async () => {
      setLoading(true);

      try {
        const { tlfId } = getConfig();

        const [upcomingRes, winnersRes] = await Promise.all([
          api.get(`${apiRoutes.OrganizationUpcomingDraws}?organizationId=${tlfId}`),
          api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`)
        ]);

        const upcomingPayload = upcomingRes?.data?.payload || upcomingRes?.data;
        const upcomingData = upcomingPayload?.data || upcomingPayload?.Data || upcomingPayload || [];

        const winnersPayload = winnersRes?.data?.payload || winnersRes?.data;
        const winnersData = winnersPayload?.data || winnersPayload?.Data || winnersPayload || [];

        const now = new Date();
        const allOrgDraws = Array.isArray(upcomingData) ? upcomingData : [];

        const futureDraws = [];
        const expiredDraws = [];

        allOrgDraws.forEach((draw) => {
          const parsedDate = parseDrawDate(draw);
          const drawStatus = (draw.status || draw.Status || '').toLowerCase();

          if (parsedDate && parsedDate < now && drawStatus === 'undrawn') {
            expiredDraws.push(draw);
          } else {
            futureDraws.push(draw);
          }
        });

        const mappedUpcoming = futureDraws.map(formatUpcoming);

        const winnerResults = mapWinnersFromApi(winnersData).map((w, i) => formatWinnerAsPast(w, i, allOrgDraws));
        const expiredResults = expiredDraws.map(formatExpiredAsPast);
        const allPastResults = [...winnerResults, ...expiredResults]
          .sort((a, b) => b.sortDate - a.sortDate);

        setUpcomingDraws(mappedUpcoming);
        setPastDraws(allPastResults);
      } catch (error) {
        console.error('Failed to load draws:', error);
        setUpcomingDraws([]);
        setPastDraws([]);
      } finally {
        setLoading(false);
      }
    };

    loadDraws();
  }, []);

  return (
    <DrawsPageContext.Provider value={{
      activeTab,
      setActiveTab,
      upcomingDraws,
      pastDraws,
      loading
    }}>
      {children}
    </DrawsPageContext.Provider>
  );
}

export function useDrawsPage() {
  const context = useContext(DrawsPageContext);
  if (!context) {
    throw new Error('useDrawsPage must be used within DrawsPageProvider');
  }
  return context;
}
